import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import useBusinessResource from "../hooks/useBusinessResource";
import type { BusinessScope } from "../utils/businessResourceFreshness";

interface Options extends BusinessScope {
  active: boolean;
  enabled?: boolean;
  paymentType: "in" | "out";
  includeEntities?: boolean;
}
const empty = { entities: [] as any[], paymentModes: [] as any[], treasuryAccounts: [] as any[] };

export default function usePaymentFormChoices({ active, enabled = true,
  paymentType, includeEntities = true, ...scope }: Options) {
  const resource = useBusinessResource({
    ...scope, resource: "payment-form-choices", parameters: [paymentType, includeEntities],
    enabled: active && enabled, active,
    load: async (signal) => {
      const config = { signal, headers: { "X-Company-Id": scope.companyId } };
      const [entities, modes, accounts] = await Promise.all([
        includeEntities ? apiClient.get(API_CONFIG.apiUrl(`/users/${paymentType === "out" ? "suppliers" : "customers"}`), {
          ...config, params: { companyId: scope.companyId, warehouseId: scope.warehouseId },
        }) : Promise.resolve({ data: [] }),
        apiClient.get(API_CONFIG.apiUrl("/payment-modes"), { ...config,
          params: { company_id: scope.companyId, page: 1, limit: 200 } }),
        apiClient.get(API_CONFIG.apiUrl("/treasury/accounts"), { ...config,
          params: { company_id: scope.companyId, warehouse_id: scope.warehouseId,
            include_inactive: false, module_scope: scope.moduleScope || "commercial" } }),
      ]);
      const paymentModes = Array.isArray(modes.data) ? modes.data
        : modes.data?.paymentModes || modes.data?.payment_modes;
      const treasuryAccounts = accounts.data?.data || accounts.data?.accounts || accounts.data;
      if (!Array.isArray(entities.data) || !Array.isArray(paymentModes) || !Array.isArray(treasuryAccounts)) {
        throw new Error("Réponse des listes de choix invalide.");
      }
      const allModes = [...paymentModes];
      // This API defaults to ten rows. Do not silently lose an older configured
      // method if the company has more than a page of payment modes.
      for (let page = 2; page <= Number(modes.data?.pagination?.totalPages || 1); page++) {
        const response = await apiClient.get(API_CONFIG.apiUrl("/payment-modes"), {
          ...config, params: { company_id: scope.companyId, page, limit: 200 },
        });
        if (!Array.isArray(response.data?.paymentModes)) throw new Error("Réponse des modes de paiement invalide.");
        allModes.push(...response.data.paymentModes);
      }
      // Reuse the existing visibility rules of the entity/account APIs, but
      // retain only the choice fields in the shared cache.
      return {
        entities: entities.data.map((row: any) => ({ id: row.id ?? row.ID_Frs,
          user_id: row.user_id ?? row.USER_ID, name: row.name ?? row.Nom_Raison_Sociale,
          company_name: row.company_name, user_type: row.user_type,
          warehouse_id: row.warehouse_id, detail_warehouse_id: row.detail_warehouse_id })),
        paymentModes: allModes.map((row: any) => ({ id: row.id, name: row.name, company_id: row.company_id })),
        treasuryAccounts: treasuryAccounts.map((row: any) => ({ id: row.id, name: row.name,
          is_active: row.is_active, company_id: row.company_id, warehouse_id: row.warehouse_id,
          module_scope: row.module_scope })),
      };
    },
  });
  return { ...resource, ...(resource.data || empty) };
}
